import { handleResponseOld, handleResponseWithNon200Errors, makeGet } from './helpers';
import shuffle from 'lodash/shuffle';

const transform = (body) => {
    if (body.payload?.itemIDs) {
        body.payload.itemIDs = shuffle(body.payload.itemIDs);
    }
    return {
        error: body.error,
        payload: body.payload,
    };
};

/**
 * @category Search API
 * @see fetchCategoryResults
 */
type FetchCategoryResultsParams = {
    authToken: string;
    deployment: string;
    ids: any;
    searchQuery: any;
};
/**
 * fetches category search results via Get to Search API
 * @function fetchCategoryResults
 * @category Search API
 * @param {FetchCategoryResultsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/search/categoryweb?ids=${encodedIds}&parameters=${encodedParams}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/p8swwvetjb/resources/rflcvi/methods/GET
 * @see https://github.com/LIVEauctioneers/search-party/tree/master/functions/CategoryWebV2
 */
// this is search ... need to adjust to use the category landing page endpoint
const fetchCategoryResults = ({ authToken, deployment, ids, searchQuery }: FetchCategoryResultsParams) =>
    new Promise<any>((resolve, reject) => {
        const encodedIds = encodeURIComponent(JSON.stringify(ids));
        let buyNow = '';
        if (searchQuery.buyNow) {
            buyNow = `&buyNow=${encodeURIComponent(JSON.stringify(searchQuery.buyNow))}`;
            delete searchQuery.buyNow;
        }
        let searchString = `<SEARCH-API>?ids=${encodedIds}${buyNow}`;
        const encodedParams = encodeURIComponent(JSON.stringify(searchQuery));
        searchString = `${searchString}&parameters=${encodedParams}`;

        const request = makeGet({ apiPath: 'categoryweb', authToken, deployment, path: searchString });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Search API
 * @see fetchPromotedSearchResults
 */
type FetchPromotedSearchResultsParams = {
    authToken: string;
    deployment: string;
    ids?: any;
    searchQuery: any;
};
/**
 * fetches promoted search results via Get to Search API
 * @function fetchPromotedSearchResults
 * @category Search API
 * @param {FetchPromotedSearchResultsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/search/categoryweb?ids=${encodedIds}&parameters=${encodedParams}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/p8swwvetjb/resources/rflcvi/methods/GET
 * @see https://github.com/LIVEauctioneers/search-party/tree/master/functions/CategoryWebV2
 */
const fetchPromotedSearchResults = ({ authToken, deployment, ids, searchQuery }: FetchPromotedSearchResultsParams) =>
    new Promise<any>((resolve, reject) => {
        // temp for phased rollout of search
        let searchString = `<SEARCH-API>?promoted=1&parameters=${encodeURIComponent(JSON.stringify(searchQuery))}`;

        let apiPath = 'web';
        if (ids && ids.length) {
            searchString = `${searchString}&ids=${encodeURIComponent(JSON.stringify(ids))}`;
            apiPath = 'categoryweb';
        } else if (searchQuery.campaignId && searchQuery.campaignId > 0) {
            searchString = `${searchString}&ids=${encodeURIComponent('[]')}`;
            apiPath = 'categoryweb';
        }

        const request = makeGet({ apiPath, authToken, deployment, path: searchString });

        request.end((err, response) => handleResponseOld({ err, reject, resolve, response, transform }));
    });

/**
 * @category Search API
 * @see fetchSearchFacetsResults
 */
type FetchSearchFacetsResultsParams = {
    authToken: string;
    deployment: string;
    searchQuery: any;
};
/**
 * Fetches a faceted set of search results via Get to Search API
 * @function fetchSearchFacetsResults
 * @category Search API
 * @param {FetchSearchFacetsResultsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/search/faceted?parameters=${encodedParams}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/p8swwvetjb/resources/qrublf/methods/GET
 * @see https://github.com/LIVEauctioneers/search-party/tree/master/functions/SearchApiV2
 */
const fetchSearchFacetsResults = ({ authToken, deployment, searchQuery }: FetchSearchFacetsResultsParams) =>
    new Promise<any>((resolve, reject) => {
        let searchString = `<SEARCH-API>?parameters=${encodeURIComponent(JSON.stringify(searchQuery))}`;

        const request = makeGet({
            apiPath: 'faceted',
            authToken,
            deployment,
            path: searchString,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Search API
 * @see fetchSearchResults
 */
type FetchSearchResultsParams = {
    authToken: string;
    deployment: string;
    geoCoordinates: any;
    searchQuery: any;
    useGeoLocation: boolean;
};
/**
 * Fetches search results via Get to Search API
 * @function fetchSearchResults
 * @category Search API
 * @param {FetchSearchResultsParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/search/web?parameters=${encodedParams}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/p8swwvetjb/resources/bnqcz6/methods/GET
 * @see https://github.com/LIVEauctioneers/search-party/tree/master/functions/SearchWebV2
 */
const fetchSearchResults = ({
    authToken,
    deployment,
    geoCoordinates,
    searchQuery,
    useGeoLocation = false,
}: FetchSearchResultsParams) =>
    new Promise<any>((resolve, reject) => {
        let buyNow = '';
        if (searchQuery.buyNow) {
            buyNow = `&buyNow=${encodeURIComponent(JSON.stringify(searchQuery.buyNow))}`;
            delete searchQuery.buyNow;
        }
        if (useGeoLocation) {
            searchQuery.useGeo = true;
        }

        if (Boolean(geoCoordinates?.latitude) && Boolean(geoCoordinates?.longitude)) {
            searchQuery.lat = `${geoCoordinates.latitude}`;
            searchQuery.long = `${geoCoordinates.longitude}`;
        }

        let searchString = `<SEARCH-API>?parameters=${encodeURIComponent(JSON.stringify(searchQuery))}${buyNow}`;
        const request = makeGet({ apiPath: 'web', authToken, deployment, path: searchString, useCacheKey: false });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

export default {
    fetchCategoryResults,
    fetchPromotedSearchResults,
    fetchSearchFacetsResults,
    fetchSearchResults,
};
